import { useState } from 'react';
import { formatMinor } from '../../../shared/utils/money';
import {
  useCharterAdminBookings,
  useCharterAdminOrders,
  useCharterAdminWaitlist,
} from '../hooks/useCharter';
import {
  BookingStatusBadge,
  PaymentStatusBadge,
  WaitlistStatusBadge,
} from '../components/CharterStatusBadge';
import {
  CharterHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  TableCard,
  TD,
  TD_MUTED,
  TH,
} from '../components/CharterPage';
import { formatWat, formatWatDate } from '../components/charterFormat';

type Tab = 'bookings' | 'orders' | 'waitlist';

const hasIntake = (formData?: Record<string, unknown> | null) => !!formData && Object.keys(formData).length > 0;

/**
 * TCS-admin: read-only cross-provider oversight of Charter bookings, orders and
 * waitlist. Refund decisions belong to the merchant — this view only audits.
 */
export function CharterOversightPage() {
  const [tab, setTab] = useState<Tab>('bookings');
  const bookingsQuery = useCharterAdminBookings();
  const ordersQuery = useCharterAdminOrders();
  const waitlistQuery = useCharterAdminWaitlist();

  const bookings = bookingsQuery.data ?? [];
  const orders = ordersQuery.data ?? [];
  const waitlist = waitlistQuery.data ?? [];

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <CharterHeader
        title="Charter · Oversight"
        subtitle="Read-only cross-provider view of bookings, orders and waitlist. Refunds are merchant decisions — shown here for audit only."
      />

      <div className="flex gap-6 border-b border-border mb-4">
        {(
          [
            ['bookings', 'Bookings'],
            ['orders', 'Orders'],
            ['waitlist', 'Waitlist'],
          ] as [Tab, string][]
        ).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={`pb-2 -mb-px text-sm font-medium border-b-2 transition-colors ${
              tab === value
                ? 'border-[#28282A] text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'bookings' &&
        (bookingsQuery.isLoading ? (
          <LoadingState label="Loading bookings…" />
        ) : bookingsQuery.error ? (
          <ErrorState label="Failed to load bookings." />
        ) : bookings.length === 0 ? (
          <EmptyState label="No bookings." />
        ) : (
          <TableCard>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted text-left">
                  <th className={TH}>Customer</th>
                  <th className={TH}>Status</th>
                  <th className={TH}>Payment</th>
                  <th className={TH}>Slot (WAT)</th>
                  <th className={TH}>Deposit / balance</th>
                  <th className={TH}>Intake</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {bookings.map((b) => (
                  <tr key={b.id} className="hover:bg-muted transition-colors">
                    <td className={TD}>
                      <span className="font-medium">{b.customer?.name ?? '—'}</span>
                      <span className="block text-xs text-muted-foreground">{b.customer?.email}</span>
                    </td>
                    <td className="px-6 py-4"><BookingStatusBadge status={b.status} /></td>
                    <td className="px-6 py-4"><PaymentStatusBadge status={b.paymentStatus} /></td>
                    <td className={TD_MUTED}>{b.slot ? formatWat(b.slot.startsAt) : 'Unscheduled'}</td>
                    <td className={TD_MUTED}>
                      {b.deposit ? formatMinor(b.deposit.amount, b.deposit.currency) : '—'}
                      {b.remainingBalance ? ` / ${formatMinor(b.remainingBalance, b.currency)}` : ''}
                    </td>
                    <td className={TD_MUTED}>{hasIntake(b.formData) ? 'Submitted' : 'Pending'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        ))}

      {tab === 'orders' &&
        (ordersQuery.isLoading ? (
          <LoadingState label="Loading orders…" />
        ) : ordersQuery.error ? (
          <ErrorState label="Failed to load orders." />
        ) : orders.length === 0 ? (
          <EmptyState label="No orders." />
        ) : (
          <TableCard>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted text-left">
                  <th className={TH}>Customer</th>
                  <th className={TH}>Status</th>
                  <th className={TH}>Payment</th>
                  <th className={TH}>Fulfilment</th>
                  <th className={TH}>Deposit / balance</th>
                  <th className={TH}>Intake</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {orders.map((o) => (
                  <tr key={o.id} className="hover:bg-muted transition-colors">
                    <td className={TD}>
                      <span className="font-medium">{o.customer?.name ?? '—'}</span>
                      <span className="block text-xs text-muted-foreground">{o.customer?.email}</span>
                    </td>
                    <td className="px-6 py-4"><BookingStatusBadge status={o.status} /></td>
                    <td className="px-6 py-4"><PaymentStatusBadge status={o.paymentStatus} /></td>
                    <td className={TD_MUTED}>
                      {o.requiresSlot === false ? 'Async' : o.slot ? formatWat(o.slot.startsAt) : 'Unscheduled'}
                    </td>
                    <td className={TD_MUTED}>
                      {o.deposit ? formatMinor(o.deposit.amount, o.deposit.currency) : '—'}
                      {o.remainingBalance ? ` / ${formatMinor(o.remainingBalance, o.currency)}` : ''}
                    </td>
                    <td className={TD_MUTED}>{hasIntake(o.formData) ? 'Submitted' : 'Pending'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        ))}

      {tab === 'waitlist' &&
        (waitlistQuery.isLoading ? (
          <LoadingState label="Loading waitlist…" />
        ) : waitlistQuery.error ? (
          <ErrorState label="Failed to load waitlist." />
        ) : waitlist.length === 0 ? (
          <EmptyState label="No waitlist signups." />
        ) : (
          <TableCard>
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted text-left">
                  <th className={TH}>Customer</th>
                  <th className={TH}>Note</th>
                  <th className={TH}>Signed up</th>
                  <th className={TH}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {waitlist.map((e) => (
                  <tr key={e.id} className="hover:bg-muted transition-colors">
                    <td className={TD}>
                      <span className="font-medium">{e.customer.name}</span>
                      <span className="block text-xs text-muted-foreground">{e.customer.email}</span>
                    </td>
                    <td className={`${TD_MUTED} max-w-[240px] truncate`} title={e.note ?? ''}>{e.note ?? '—'}</td>
                    <td className={TD_MUTED}>{formatWatDate(e.createdAt)}</td>
                    <td className="px-6 py-4"><WaitlistStatusBadge status={e.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        ))}
    </div>
  );
}
